/**
 * Search Filters - Complete Rewrite
 * Modern, performant filter system for search results
 */

class SearchFilters extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('#SearchFiltersForm');
    this.debounceTimer = null;
    this.isUpdating = false;
    this.init();
  }

  init() {
    if (!this.form) return;

    // Initialize filter group toggles
    this.initFilterGroups();

    // Handle checkbox changes
    this.form.addEventListener('change', (e) => {
      if (e.target.classList.contains('search-filters__checkbox-input')) {
        this.handleFilterChange();
      }
    });

    // Handle price range inputs with debounce
    const priceInputs = this.form.querySelectorAll('.search-filters__price-input');
    priceInputs.forEach(input => {
      input.addEventListener('input', () => {
        this.debounceFilterChange();
      });
      
      input.addEventListener('blur', () => {
        this.validatePriceInput(input);
      });
    });

    // Handle sort dropdown
    const sortSelect = this.form.querySelector('#SearchSortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        this.handleFilterChange();
      });
    }

    // Handle active filter removal
    this.initActiveFilters();

    // Handle clear all button
    const clearBtn = this.querySelector('.search-filters__clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }

    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.handlePopState();
    });
  }

  initFilterGroups() {
    const toggles = this.querySelectorAll('.search-filters__group-toggle');
    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const content = this.querySelector(`#${toggle.getAttribute('aria-controls')}`);
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        
        toggle.setAttribute('aria-expanded', !isExpanded);
        content.classList.toggle('is-open');
      });
    });
  }

  initActiveFilters() {
    const activeItems = this.querySelectorAll('.search-filters__active-item');
    activeItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        this.removeFilter(item);
      });
    });
  }

  debounceFilterChange() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.handleFilterChange();
    }, 600);
  }

  validatePriceInput(input) {
    const value = parseFloat(input.value);
    const max = parseFloat(input.getAttribute('max'));
    const min = parseFloat(input.getAttribute('min'));

    if (input.value && !isNaN(value)) {
      if (value < min) {
        input.value = min;
      } else if (value > max) {
        input.value = max;
      }
    }
  }

  createSearchParams() {
    const formData = new FormData(this.form);
    const params = new URLSearchParams();

    // Preserve search query
    const searchQuery = formData.get('q');
    if (searchQuery) {
      params.set('q', searchQuery);
    }

    // Preserve options prefix
    const optionsPrefix = formData.get('options[prefix]');
    if (optionsPrefix) {
      params.set('options[prefix]', optionsPrefix);
    }

    // Add sort_by
    const sortBy = formData.get('sort_by');
    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    // Add filter parameters
    formData.forEach((value, key) => {
      if (key === 'q' || key === 'options[prefix]' || key === 'sort_by') return;
      if (value && value.toString().trim() !== '') {
        params.append(key, value);
      }
    });

    return params.toString();
  }

  handleFilterChange() {
    if (this.isUpdating) return;
    
    const searchParams = this.createSearchParams();
    this.updateResults(searchParams);
  }

  removeFilter(item) {
    const filterName = item.dataset.filterName;
    const filterValue = item.dataset.filterValue;
    const filterType = item.dataset.filterType;

    if (filterType === 'price_range') {
      // Clear price range inputs
      const priceInputs = this.form.querySelectorAll(`input[name^="${filterName}"]`);
      priceInputs.forEach(input => {
        input.value = '';
      });
    } else {
      // Uncheck checkbox
      const checkbox = this.form.querySelector(
        `input[name="${filterName}"][value="${filterValue}"]`
      );
      if (checkbox) {
        checkbox.checked = false;
        checkbox.closest('.search-filters__checkbox')?.classList.remove('is-checked');
      }
    }

    this.handleFilterChange();
  }

  clearAllFilters() {
    // Uncheck all checkboxes
    const checkboxes = this.form.querySelectorAll('.search-filters__checkbox-input:checked');
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
      checkbox.closest('.search-filters__checkbox')?.classList.remove('is-checked');
    });

    // Clear price inputs
    const priceInputs = this.form.querySelectorAll('.search-filters__price-input');
    priceInputs.forEach(input => {
      input.value = '';
    });

    this.handleFilterChange();
  }

  async updateResults(searchParams) {
    if (this.isUpdating) return;
    
    this.isUpdating = true;
    const sectionId = this.dataset.sectionId;
    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;

    this.showLoading();

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product grid
      this.updateProductGrid(doc);

      // Update filters
      this.updateFilters(doc);

      // Update product count
      this.updateProductCount(doc);

      // Update URL without page reload
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}?${searchParams}`
      );

      // Scroll to top of results
      this.scrollToResults();

    } catch (error) {
      console.error('Error updating search results:', error);
      this.showError('Failed to update results. Please try again.');
    } finally {
      this.hideLoading();
      this.isUpdating = false;
    }
  }

  updateProductGrid(doc) {
    const newGrid = doc.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('ProductGridContainer');

    if (newGrid && currentGrid) {
      // Preserve scroll position if needed
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      currentGrid.innerHTML = newGrid.innerHTML;
      
      // Trigger any necessary re-initializations
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('search:updated'));
      }
    }
  }

  updateFilters(doc) {
    const newFilters = doc.querySelector('search-filters');
    if (!newFilters) return;

    // Preserve open/closed state of filter groups
    const openStates = {};
    const currentGroups = this.querySelectorAll('.search-filters__group');
    currentGroups.forEach(group => {
      const toggle = group.querySelector('.search-filters__group-toggle');
      if (toggle) {
        const contentId = toggle.getAttribute('aria-controls');
        const content = group.querySelector(`#${contentId}`);
        if (content && content.classList.contains('is-open')) {
          openStates[contentId] = true;
        }
      }
    });

    // Update filter form
    const newForm = newFilters.querySelector('#SearchFiltersForm');
    const currentForm = this.querySelector('#SearchFiltersForm');
    if (newForm && currentForm) {
      currentForm.innerHTML = newForm.innerHTML;
    }

    // Restore open states
    Object.keys(openStates).forEach(contentId => {
      const content = this.querySelector(`#${contentId}`);
      const toggle = this.querySelector(`[aria-controls="${contentId}"]`);
      if (content && toggle) {
        content.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });

    // Update active filters
    const newActiveFilters = newFilters.querySelector('.search-filters__active');
    const currentActiveFilters = this.querySelector('.search-filters__active');

    if (newActiveFilters) {
      if (currentActiveFilters) {
        currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
      } else {
        this.appendChild(newActiveFilters);
      }
    } else if (currentActiveFilters) {
      currentActiveFilters.remove();
    }

    // Re-initialize event listeners
    this.initFilterGroups();
    this.initActiveFilters();
  }

  updateProductCount(doc) {
    const newCountElement = doc.querySelector('#ProductCount');
    const currentCountElement = document.querySelector('#ProductCount');

    if (newCountElement && currentCountElement) {
      currentCountElement.innerHTML = newCountElement.innerHTML;
    }
  }

  showLoading() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) {
      productGrid.classList.add('is-loading');
    }

    // Show loading overlay if it exists
    const loadingOverlay = productGrid?.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('hidden');
    }
  }

  hideLoading() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) {
      productGrid.classList.remove('is-loading');
    }

    const loadingOverlay = productGrid?.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
  }

  showError(message) {
    // You can implement a toast notification here
    console.error(message);
  }

  scrollToResults() {
    const productGrid = document.getElementById('ProductGridContainer');
    if (productGrid) {
      const offset = productGrid.getBoundingClientRect().top + window.pageYOffset - 20;
      window.scrollTo({
        top: offset,
        behavior: 'smooth'
      });
    }
  }

  handlePopState() {
    const searchParams = window.location.search.slice(1);
    if (searchParams) {
      this.updateResults(searchParams);
    }
  }
}

// Register custom element
if (!customElements.get('search-filters')) {
  customElements.define('search-filters', SearchFilters);
}
